import { mutations } from 'entities';

import { Alert, Box, Button, Column, Row } from 'components/Blocks';
import { MailIcon } from 'components/Blocks/Icons';
import { Coin } from 'components/DecorativeIcons';

import { useUser } from 'providers/Auth';
import { useLang } from 'locale';
import { env } from 'config/env';

import { useIsBreakpoint } from 'hooks';

import type { EmailConfirmReason } from '.';
import { truncateText } from 'utils/text';

interface SendEmailFormProps {
  onSent: () => void;
  rewardPoints: number;
  reason?: EmailConfirmReason;
}

export function SendEmailForm({ onSent, rewardPoints, reason }: SendEmailFormProps) {
  const { t } = useLang();
  const { email } = useUser();

  const isMobile = useIsBreakpoint('PHONE');

  const sendCode = mutations.users.sendEmailConfirmation.use({
    onSuccess: onSent,
  });

  return (
    <Column gap="6">
      <Box
        className="mx-auto w-fit flex-grow-0 bg-primary p-3 text-primary-foreground"
        border="circle"
      >
        <MailIcon size={24} />
      </Box>

      <h2 className="text-center text-xl font-semibold text-primary">{t('verify-email')}</h2>

      <Column>
        <Row gap="2" justifyContent="center">
          <Coin size={24} />
          <span className="text-xl font-semibold text-warning">
            {rewardPoints} {t('points')}
          </span>
        </Row>

        {reason === 'REDEMPTION' && (
          <p className="mx-auto max-w-[80%] text-center font-semibold text-primary">
            {t('verify-email-before-redemption', { points: rewardPoints })}
          </p>
        )}

        <p className="text-center">
          {t('send-code-to')} {isMobile && <br />}
          <strong className='break-all'>{truncateText(email, 45)}</strong>
        </p>
        <Alert level="warning" type="lightly-filled">
          {t('wrong-email-disclaimer')}{' '}
          <a
            href={env.supportUrl}
            target="support"
            rel="noopener noreferrer"
            className="font-semibold underline"
          >
            {t('support')}
          </a>
          .
        </Alert>
        <Button {...sendCode.buttonProps} onClick={sendCode.execute}>
          {t('send')}
        </Button>
      </Column>
    </Column>
  );
}
